import { baseApi } from "../api/baseApi";
import { filterJacketJobsOpenOrClosed } from "../../app/utils/filterJacketJobsOpenOrClosed";

/** Accepts a JSON array or common wrapper shapes from proxies / older APIs. */
function asContractServiceArray(response) {
  if (Array.isArray(response)) {
    return response;
  }
  if (response && typeof response === "object") {
    const nested =
      response.data ??
      response.items ??
      response.content ??
      response.results ??
      response.rows ??
      response.services;
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  return [];
}

function mapEmployeeOptionRows(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  return arr
    .map((row) => ({
      empId: row.empId ?? row.emp_id,
      displayName: String(row.displayName ?? row.display_name ?? "").trim(),
    }))
    .filter((row) => row.empId != null && row.displayName !== "");
}

function normalizeApiDateField(value) {
  if (value == null || value === "") {
    return "";
  }
  const s = String(value).trim();
  return s.includes("T") ? s.split("T")[0] : s;
}

function normalizeContractBillingCycle(raw) {
  if (raw == null || raw === "") {
    return null;
  }
  return String(raw).trim();
}

/** {@code act_inv_profile.active_flag}; null/undefined → active (legacy rows). */
function normalizeInvProfileActiveFlag(row) {
  const raw = row?.activeFlag ?? row?.active_flag;
  if (raw === false || raw === "false" || raw === 0 || raw === "0") {
    return false;
  }
  return true;
}

function normalizeInvProfileCompanyNumbers(row) {
  const fromChild = Array.isArray(row?.companyNumbers) ? row.companyNumbers : [];
  const cleaned = fromChild.map((n) => String(n ?? "").trim()).filter(Boolean);
  if (cleaned.length > 0) {
    return cleaned;
  }
  const legacy = String(row?.companyNumber ?? row?.company_number ?? "").trim();
  if (!legacy) {
    return [];
  }
  return legacy
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean);
}

function normalizeInvProfileApiRow(row) {
  const companyNumbers = normalizeInvProfileCompanyNumbers(row);
  return {
    invProfileId: row.invProfileId ?? row.inv_profile_id,
    custId: row.custId ?? row.custid ?? null,
    custgroup: row.custgroup ?? "",
    clientName: row.clientName ?? row.client_name ?? "",
    clientCode: row.clientCode ?? row.client_code ?? "",
    invoiceNameProgram: String(
      row.invoiceNameProgram ?? row.invoice_program_name ?? ""
    ).trim(),
    companyNumbers,
    companyNumber:
      companyNumbers.length > 0
        ? companyNumbers.join(", ")
        : String(row.companyNumber ?? row.company_number ?? "").trim(),
    invPoNum: String(row.invPoNum ?? row.inv_ponum ?? "").trim(),
    activeFlag: normalizeInvProfileActiveFlag(row),
  };
}

/** Normalizes invoice profile detail rows from the billing API (list + create/update). */
function normalizeInvProfileDetailApiRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  return {
    invProfileDetailId: row.invProfileDetailId ?? row.inv_profile_detail_id,
    invProfileId: row.invProfileId ?? row.inv_profile_id,
    contractId: row.contractId ?? row.contract_id,
    contractServiceId: row.contractServiceId ?? row.contract_service_id,
    servicesRendered: String(row.servicesRendered ?? row.services_rendered ?? "").trim(),
    category: String(row.category ?? "").trim(),
    defaultInvoiceDescription: String(
      row.defaultInvoiceDescription ?? row.default_invoice_description ?? ""
    ).trim(),
    sequenceDisplay:
      row.sequenceDisplay ?? row.sequence_display ?? null,
    basedOn: String(row.basedOn ?? row.based_on ?? "").trim(),
    clientRate: row.clientRate ?? row.client_rate ?? row.contractRate ?? row.contract_rate ?? null,
    laborPercent: row.laborPercent ?? row.labor_percent ?? null,
    effectiveDate: normalizeApiDateField(row.effectiveDate ?? row.effective_date),
    endDate: normalizeApiDateField(row.endDate ?? row.end_date),
    endAppliedByDisplayName: String(
      row.endAppliedByDisplayName ?? row.end_applied_by_display_name ?? ""
    ).trim(),
    billingDataClassification: String(
      row.billingDataClassification ?? row.billing_data_classification ?? ""
    ).trim(),
    billingDataClassificationValue: String(
      row.billingDataClassificationValue ?? row.billing_data_classification_value ?? ""
    ).trim(),
    billingDataClassifications: Array.isArray(row.billingDataClassifications)
      ? row.billingDataClassifications.map((entry) => ({
          companyNumber: String(entry.companyNumber ?? entry.company_number ?? "").trim(),
          billingDataClassification: String(
            entry.billingDataClassification ?? entry.billing_data_classification ?? ""
          ).trim(),
          billingDataClassificationValue: String(
            entry.billingDataClassificationValue ??
              entry.billing_data_classification_value ??
              ""
          ).trim(),
        }))
      : [],
    opCodes: Array.isArray(row.opCodes ?? row.op_codes)
      ? (row.opCodes ?? row.op_codes)
          .map((entry) => {
            if (typeof entry === "string") {
              const opCode = String(entry ?? "").trim();
              return opCode ? { opCode, jobNo: "", companyNumber: "" } : null;
            }
            const opCode = String(entry?.opCode ?? entry?.op_code ?? "").trim();
            if (!opCode) {
              return null;
            }
            return {
              opCode,
              jobNo: String(entry?.jobNo ?? entry?.job_no ?? "").trim(),
              companyNumber: String(entry?.companyNumber ?? entry?.company_number ?? "").trim(),
            };
          })
          .filter(Boolean)
      : [],
  };
}

export const clientConfigurationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getClientConfigurationGroups: build.query({
      query: () => ({
        url: "/api/client-configuration/groups",
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => String(row ?? "").trim()).filter((row) => row.length > 0);
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationGroupDetails: build.query({
      query: (group) => ({
        url: "/api/client-configuration/group-details",
        method: "GET",
        params: { group },
      }),
    }),
    getClientConfigurationCustomer: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}`,
        method: "GET",
      }),
      providesTags: (result, error, custId) =>
        custId != null ? [{ type: "ClientConfigurationCustomer", id: custId }] : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    updateClientConfigurationCustomer: build.mutation({
      query: ({ custId, body }) => ({
        url: `/api/client-configuration/customers/${custId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationCustomer", id: custId }] : [],
    }),
    getClientConfigurationEmployees: build.query({
      query: () => ({
        url: "/api/employees",
        method: "GET",
      }),
    }),
    getClientConfigurationCustgroupAccountExecutives: build.query({
      query: (group) => ({
        url: "/api/client-configuration/custgroup-account-executives",
        method: "GET",
        params: { group: group ?? "" },
      }),
      transformResponse: (response) => mapEmployeeOptionRows(response),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustgroupCustomerTeamOptions: build.query({
      query: (group) => ({
        url: "/api/client-configuration/custgroup-customer-team-options",
        method: "GET",
        params: { group: group ?? "" },
      }),
      transformResponse: (response) => ({
        teamManagers: mapEmployeeOptionRows(response?.teamManagers),
        billingCoordinators: mapEmployeeOptionRows(response?.billingCoordinators),
      }),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationJobTypes: build.query({
      query: () => ({
        url: "/api/client-configuration/job-types",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationMasterTypes: build.query({
      query: () => ({
        url: "/api/client-configuration/master-types",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationSubTypes: build.query({
      query: () => ({
        url: "/api/client-configuration/sub-types",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationJobStatuses: build.query({
      query: () => ({
        url: "/api/client-configuration/job-statuses",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationCostCenters: build.query({
      query: () => ({
        url: "/api/client-configuration/cost-centers",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationProducts: build.query({
      query: () => ({
        url: "/api/client-configuration/products",
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getClientConfigurationCustgroupsDirectory: build.query({
      query: ({
        page = 1,
        size = 20,
        search = "",
        status = "all",
        includeCounts = true,
      } = {}) => ({
        url: "/api/client-configuration/custgroups-directory",
        method: "GET",
        params: {
          page,
          size,
          search: search?.trim() ? search.trim() : undefined,
          status: status || "all",
          includeCounts,
        },
      }),
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          const items = response.map((row) => ({
            clientName: row.clientName ?? row.client_name ?? "",
            clientCode: row.clientCode ?? row.client_code ?? "",
            status: row.status ?? "",
            custId: row.custId ?? row.cust_id ?? null,
            jacketCount: row.jacketCount ?? row.jacket_count ?? 0,
            activePoCount: row.activePoCount ?? row.active_po_count ?? 0,
          }));
          return {
            items,
            page: 1,
            size: items.length,
            totalFiltered: items.length,
            totalClients: items.length,
            activeClients: items.filter((r) => r.status === "Active").length,
            totalActivePoCount: items.reduce((sum, r) => sum + (r.activePoCount || 0), 0),
          };
        }
        const items = Array.isArray(response?.items) ? response.items : [];
        return {
          items: items.map((row) => ({
            clientName: row.clientName ?? row.client_name ?? "",
            clientCode: row.clientCode ?? row.client_code ?? "",
            status: row.status ?? "",
            custId: row.custId ?? row.cust_id ?? null,
            jacketCount: row.jacketCount ?? row.jacket_count ?? 0,
            activePoCount: row.activePoCount ?? row.active_po_count ?? 0,
          })),
          page: response.page ?? 1,
          size: response.size ?? 20,
          totalFiltered: response.totalFiltered ?? response.total_filtered ?? 0,
          totalClients: response.totalClients ?? response.total_clients ?? 0,
          activeClients: response.activeClients ?? response.active_clients ?? 0,
          totalActivePoCount:
            response.totalActivePoCount ?? response.total_active_po_count ?? 0,
        };
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustgroupClientOptions: build.query({
      query: () => ({
        url: "/api/client-configuration/custgroup-client-options",
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows
          .map((row) => ({
            custgroup: String(row.custgroup ?? "").trim(),
            clientName: String(row.clientName ?? row.client_name ?? "").trim(),
          }))
          .filter((row) => row.custgroup.length > 0);
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationJackets: build.query({
      query: (group) => ({
        url: "/api/client-configuration/jackets",
        method: "GET",
        params: { group },
      }),
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return { jobNos: response };
        }
        return { jobNos: response?.jobNos ?? [] };
      },
    }),
    getClientConfigurationActivePos: build.query({
      query: (group) => ({
        url: "/api/client-configuration/active-pos",
        method: "GET",
        params: { group },
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.pos)
            ? response.pos
            : [];
        return {
          pos: rows
            .map((row) => ({
              poId: row.poId ?? row.po_id ?? null,
              clientPo: String(row.clientPo ?? row.client_po ?? "").trim(),
            }))
            .filter((row) => row.clientPo.length > 0),
        };
      },
    }),
    getClientConfigurationCustomerJacketJobs: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/jacket-jobs`,
        method: "GET",
      }),
      transformResponse: (response) => ({
        jobs: filterJacketJobsOpenOrClosed(response?.jobs ?? []),
      }),
      providesTags: (result, error, custId) =>
        custId != null ? [{ type: "ClientConfigurationJacketJobs", id: custId }] : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    createClientConfigurationJacketJob: build.mutation({
      query: ({ custId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/jacket-jobs`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationJacketJobs", id: custId }] : [],
    }),
    updateClientConfigurationJacketJob: build.mutation({
      query: ({ custId, jobNo, body }) => ({
        url: `/api/client-configuration/customers/${custId}/jacket-jobs/${encodeURIComponent(jobNo)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationJacketJobs", id: custId }] : [],
    }),
    deleteClientConfigurationJacketJob: build.mutation({
      query: ({ custId, jobNo }) => ({
        url: `/api/client-configuration/customers/${custId}/jacket-jobs/${encodeURIComponent(jobNo)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationJacketJobs", id: custId }] : [],
    }),
    getClientConfigurationCustomerCompanyNumbers: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/company-numbers`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.filter((v) => {
          const s = String(v ?? "").trim();
          return s.length > 0 && /^\d/.test(s);
        });
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationProductDivisionsForCompanyNumber: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/product-divisions`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          productDivisions: Array.isArray(body.productDivisions)
            ? body.productDivisions
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationProductClassesForCompanyNumber: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/product-classes`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          productClasses: Array.isArray(body.productClasses)
            ? body.productClasses
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationProductCodesForCompanyNumber: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/product-codes`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          productCodes: Array.isArray(body.productCodes)
            ? body.productCodes
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationProductTypesForCompanyNumber: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/product-types`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          productTypes: Array.isArray(body.productTypes)
            ? body.productTypes
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationProductLinesForCompanyNumber: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/product-lines`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          productLines: Array.isArray(body.productLines)
            ? body.productLines
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationBusinessClassifications: build.query({
      query: ({ custId, companyNumber }) => ({
        url: `/api/client-configuration/customers/${custId}/business-classifications`,
        method: "GET",
        params: { companyNumber: String(companyNumber ?? "").trim() },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          billingClassNames: Array.isArray(body.billingClassNames)
            ? body.billingClassNames
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationBusinessClassificationValues: build.query({
      query: ({ custId, companyNumber, billingClassName }) => ({
        url: `/api/client-configuration/customers/${custId}/business-classification-values`,
        method: "GET",
        params: {
          companyNumber: String(companyNumber ?? "").trim(),
          billingClassName: String(billingClassName ?? "").trim(),
        },
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          companyNumber: String(body.companyNumber ?? "").trim(),
          billingClassName: String(body.billingClassName ?? "").trim(),
          billingClassValues: Array.isArray(body.billingClassValues)
            ? body.billingClassValues
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationWarehouses: build.query({
      query: () => ({
        url: "/api/client-configuration/warehouses",
        method: "GET",
      }),
      transformResponse: (response) => {
        const body = response && typeof response === "object" ? response : {};
        return {
          warehouses: Array.isArray(body.warehouses)
            ? body.warehouses
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            : [],
        };
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustomerPoOptions: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/po-options`,
        method: "GET",
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustomerContracts: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/contracts`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => ({
          id: row.contractId ?? row.contract_id,
          clientId: row.custId ?? row.cust_id,
          clientName: row.clientName ?? "",
          contractName: row.contractName ?? "",
          subContract: row.subContract ?? "",
          contractVersion: row.contractVersion ?? "",
          effectiveDate: row.effectiveDate ?? "",
          endDate: row.endDate ?? "",
          status: row.status ?? "",
          billingCycle: normalizeContractBillingCycle(row.billingCycle ?? row.billing_cycle),
          netTerms: row.netTerms ?? null,
          uploadedDocument: row.uploadedDocument ?? "",
          notes: row.notes ?? "",
          assignedJacketIds: [],
        }));
      },
      providesTags: (result, error, custId) =>
        custId != null ? [{ type: "ClientConfigurationContracts", id: custId }] : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    createClientConfigurationContract: build.mutation({
      query: ({ custId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/contracts`,
        method: "POST",
        body,
      }),
      transformResponse: (row) => ({
        id: row.contractId ?? row.contract_id,
        clientId: row.custId ?? row.cust_id,
        clientName: row.clientName ?? "",
        contractName: row.contractName ?? "",
        subContract: row.subContract ?? "",
        contractVersion: row.contractVersion ?? "",
        effectiveDate: row.effectiveDate ?? "",
        endDate: row.endDate ?? "",
        status: row.status ?? "",
        billingCycle: normalizeContractBillingCycle(row.billingCycle ?? row.billing_cycle),
        netTerms: row.netTerms ?? null,
        uploadedDocument: row.uploadedDocument ?? "",
        notes: row.notes ?? "",
        assignedJacketIds: [],
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationContracts", id: custId }] : [],
    }),
    updateClientConfigurationContract: build.mutation({
      query: ({ custId, contractId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/contracts/${contractId}`,
        method: "PUT",
        body,
      }),
      transformResponse: (row) => ({
        id: row.contractId ?? row.contract_id,
        clientId: row.custId ?? row.cust_id,
        clientName: row.clientName ?? "",
        contractName: row.contractName ?? "",
        subContract: row.subContract ?? "",
        contractVersion: row.contractVersion ?? "",
        effectiveDate: row.effectiveDate ?? "",
        endDate: row.endDate ?? "",
        status: row.status ?? "",
        billingCycle: normalizeContractBillingCycle(row.billingCycle ?? row.billing_cycle),
        netTerms: row.netTerms ?? null,
        uploadedDocument: row.uploadedDocument ?? "",
        notes: row.notes ?? "",
        assignedJacketIds: [],
      }),
      invalidatesTags: (result, error, { custId, contractId }) => {
        const tags = [];
        if (custId != null) {
          tags.push({ type: "ClientConfigurationContracts", id: custId });
        }
        if (
          custId != null &&
          contractId != null &&
          Number.isFinite(Number(custId)) &&
          Number.isFinite(Number(contractId))
        ) {
          tags.push({
            type: "ClientConfigurationContractServices",
            id: `${Number(custId)}-${Number(contractId)}`,
          });
        }
        return tags;
      },
    }),
    deleteClientConfigurationContract: build.mutation({
      query: ({ custId, contractId }) => ({
        url: `/api/client-configuration/customers/${custId}/contracts/${contractId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { custId, contractId }) => {
        const tags = [];
        if (custId != null) {
          tags.push({ type: "ClientConfigurationContracts", id: custId });
        }
        if (
          custId != null &&
          contractId != null &&
          Number.isFinite(Number(custId)) &&
          Number.isFinite(Number(contractId))
        ) {
          tags.push({
            type: "ClientConfigurationContractServices",
            id: `${Number(custId)}-${Number(contractId)}`,
          });
        }
        return tags;
      },
    }),
    getClientConfigurationCustomerContractServices: build.query({
      query: ({ custId, contractId }) => {
        const c = Number(custId);
        const cid = Number(contractId);
        return {
          url: `/api/client-configuration/customers/${c}/contracts/${cid}/services`,
          method: "GET",
        };
      },
      transformResponse: (response) =>
        asContractServiceArray(response).map((row) => {
          const tieredPricingRaw = row.tieredPricing ?? row.tiered_pricing ?? [];
          return {
            ...row,
            effectiveDate: normalizeApiDateField(row.effectiveDate ?? row.effective_date),
            endDate: normalizeApiDateField(row.endDate ?? row.end_date),
            endAppliedByDisplayName: String(
              row.endAppliedByDisplayName ?? row.end_applied_by_display_name ?? ""
            ).trim(),
            freightBilling: String(row.freightBilling ?? row.freight_billing ?? "").trim(),
            freightPricingAdjustment: String(
              row.freightPricingAdjustment ?? row.freight_pricing_adjustment ?? ""
            ).trim(),
            freightAdjustmentPercentage:
              row.freightAdjustmentPercentage ?? row.freight_adjustment_percentage ?? null,
            tieredPricingLov: String(row.tieredPricingLov ?? row.tiered_pricing_lov ?? "").trim(),
            tieredPricing: Array.isArray(tieredPricingRaw) ? tieredPricingRaw : [],
          };
        }),
      providesTags: (result, error, { custId, contractId }) =>
        custId != null &&
        contractId != null &&
        Number.isFinite(Number(custId)) &&
        Number.isFinite(Number(contractId))
          ? [{ type: "ClientConfigurationContractServices", id: `${Number(custId)}-${Number(contractId)}` }]
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    createClientConfigurationContractService: build.mutation({
      query: ({ custId, contractId, body }) => ({
        url: `/api/client-configuration/customers/${Number(custId)}/contracts/${Number(contractId)}/services`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { custId, contractId }) =>
        custId != null &&
        contractId != null &&
        Number.isFinite(Number(custId)) &&
        Number.isFinite(Number(contractId))
          ? [{ type: "ClientConfigurationContractServices", id: `${Number(custId)}-${Number(contractId)}` }]
          : [],
    }),
    updateClientConfigurationContractService: build.mutation({
      query: ({ custId, contractId, contractServiceId, body }) => ({
        url: `/api/client-configuration/customers/${Number(custId)}/contracts/${Number(contractId)}/services/${Number(contractServiceId)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { custId, contractId }) =>
        custId != null &&
        contractId != null &&
        Number.isFinite(Number(custId)) &&
        Number.isFinite(Number(contractId))
          ? [{ type: "ClientConfigurationContractServices", id: `${Number(custId)}-${Number(contractId)}` }]
          : [],
    }),
    deleteClientConfigurationContractService: build.mutation({
      query: ({ custId, contractId, contractServiceId }) => ({
        url: `/api/client-configuration/customers/${Number(custId)}/contracts/${Number(contractId)}/services/${Number(contractServiceId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { custId, contractId }) =>
        custId != null &&
        contractId != null &&
        Number.isFinite(Number(custId)) &&
        Number.isFinite(Number(contractId))
          ? [{ type: "ClientConfigurationContractServices", id: `${Number(custId)}-${Number(contractId)}` }]
          : [],
    }),
    endClientConfigurationContractService: build.mutation({
      query: ({ custId, contractId, contractServiceId }) => ({
        url: `/api/client-configuration/customers/${Number(custId)}/contracts/${Number(contractId)}/services/${Number(contractServiceId)}/end`,
        method: "POST",
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return response;
        }
        return {
          ...response,
          effectiveDate: normalizeApiDateField(response.effectiveDate ?? response.effective_date),
          endDate: normalizeApiDateField(response.endDate ?? response.end_date),
          endAppliedByDisplayName: String(
            response.endAppliedByDisplayName ?? response.end_applied_by_display_name ?? ""
          ).trim(),
        };
      },
      invalidatesTags: (result, error, { custId, contractId }) =>
        custId != null &&
        contractId != null &&
        Number.isFinite(Number(custId)) &&
        Number.isFinite(Number(contractId))
          ? [{ type: "ClientConfigurationContractServices", id: `${Number(custId)}-${Number(contractId)}` }]
          : [],
    }),
    getClientConfigurationContractServiceInvoiceDescriptionOptions: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/contract-service-invoice-description-options`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows
          .map((row) => ({
            contractServiceId: row.contractServiceId ?? row.contract_service_id,
            contractId: row.contractId ?? row.contract_id,
            category: String(row.category ?? "").trim(),
            serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
            servicesRendered: String(row.servicesRendered ?? row.services_rendered ?? "").trim(),
            defaultInvoiceDescription: String(
              row.defaultInvoiceDescription ?? row.default_invoice_description ?? ""
            ).trim(),
            basedOn: String(row.basedOn ?? row.based_on ?? "").trim(),
            clientRate: row.clientRate ?? row.client_rate ?? null,
            startDate: String(row.startDate ?? row.start_date ?? "").trim(),
          }))
          .filter((row) => row.contractServiceId != null);
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationOperations: build.query({
      query: () => ({
        url: "/api/client-configuration/operations",
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows
          .map((row) => ({
            opCode: String(row.opCode ?? row.op_code ?? "").trim(),
            descrip: String(row.descrip ?? "").trim(),
          }))
          .filter((row) => row.opCode.length > 0);
      },
      keepUnusedDataFor: 300,
    }),
    getClientConfigurationCustgroupInvProfileDetails: build.query({
      query: ({ custgroup, invoiceProgram }) => ({
        url: "/api/client-configuration/custgroup-inv-profile-details",
        method: "GET",
        params: { group: custgroup, invoiceProgram },
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => normalizeInvProfileDetailApiRow(row));
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustgroupInvProfileJobnos: build.query({
      query: ({ custgroup, invoiceProgram }) => ({
        url: "/api/client-configuration/custgroup-inv-profile-jobnos",
        method: "GET",
        params: { group: custgroup, invoiceProgram },
      }),
      transformResponse: (response) => {
        const raw = response?.jobNos ?? response?.job_nos ?? [];
        const rows = Array.isArray(raw) ? raw : [];
        const seen = new Set();
        const jobNos = [];
        for (const row of rows) {
          const jobNo = String(row ?? "").trim();
          if (!jobNo) {
            continue;
          }
          const key = jobNo.toLowerCase();
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          jobNos.push(jobNo);
        }
        return jobNos.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustgroupInvProfiles: build.query({
      query: (custgroup) => ({
        url: "/api/client-configuration/custgroup-inv-profiles",
        method: "GET",
        params: { group: custgroup },
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => normalizeInvProfileApiRow(row));
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustomerInvProfiles: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => normalizeInvProfileApiRow(row));
      },
      providesTags: (result, error, custId) =>
        custId != null ? [{ type: "ClientConfigurationInvProfiles", id: custId }] : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustomerInvProfileJobnos: build.query({
      query: ({ custId, invProfileId }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles/${invProfileId}/jobnos`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const raw = response?.jobNos ?? response?.job_nos ?? [];
        const rows = Array.isArray(raw) ? raw : [];
        const seen = new Set();
        const jobNos = [];
        for (const row of rows) {
          const jobNo = String(row ?? "").trim();
          if (!jobNo) {
            continue;
          }
          const key = jobNo.toLowerCase();
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          jobNos.push(jobNo);
        }
        return jobNos.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientConfigurationCustomerInvProfileOpCodesForOpCode: build.query({
      query: ({ custId, invProfileId, opCode }) => ({
        url: `/api/client-configuration/customers/${Number(custId)}/inv-profiles/${Number(invProfileId)}/opcodes`,
        method: "GET",
        params: { opCode },
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => ({
          invProfileOpcodeId: row.invProfileOpcodeId ?? row.inv_profile_opcode_id ?? null,
          invProfileDetailId: row.invProfileDetailId ?? row.inv_profile_detail_id ?? null,
          opCode: String(row.opCode ?? row.op_code ?? "").trim(),
          jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
          companyNumber: String(row.companyNumber ?? row.company_number ?? "").trim(),
          invoiceDescription: String(
            row.invoiceDescription ?? row.invoice_description ?? ""
          ).trim(),
        }));
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    createClientConfigurationInvProfile: build.mutation({
      query: ({ custId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationInvProfiles", id: custId }] : [],
    }),
    getClientConfigurationCustomerInvProfileDetails: build.query({
      query: (custId) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profile-details`,
        method: "GET",
      }),
      transformResponse: (response) => {
        const rows = Array.isArray(response) ? response : [];
        return rows.map((row) => normalizeInvProfileDetailApiRow(row));
      },
      providesTags: (result, error, custId) =>
        custId != null ? [{ type: "ClientConfigurationInvProfileDetails", id: custId }] : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    createClientConfigurationInvProfileDetail: build.mutation({
      query: ({ custId, invProfileId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles/${invProfileId}/details`,
        method: "POST",
        body,
      }),
      transformResponse: (response) => normalizeInvProfileDetailApiRow(response),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationInvProfileDetails", id: custId }] : [],
    }),
    updateClientConfigurationInvProfileDetail: build.mutation({
      query: ({ custId, invProfileId, invProfileDetailId, body }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles/${invProfileId}/details/${invProfileDetailId}`,
        method: "PUT",
        body,
      }),
      transformResponse: (response) => normalizeInvProfileDetailApiRow(response),
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationInvProfileDetails", id: custId }] : [],
    }),
    deactivateClientConfigurationInvProfile: build.mutation({
      query: ({ custId, invProfileId }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles/${invProfileId}/deactivate`,
        method: "POST",
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return response;
        }
        return {
          ...response,
          activeFlag: normalizeInvProfileActiveFlag(response),
        };
      },
      invalidatesTags: (result, error, { custId }) =>
        custId != null
          ? [
              { type: "ClientConfigurationInvProfiles", id: custId },
              { type: "ClientConfigurationInvProfileDetails", id: custId },
            ]
          : [],
    }),
    endClientConfigurationInvProfileDetail: build.mutation({
      query: ({ custId, invProfileId, invProfileDetailId }) => ({
        url: `/api/client-configuration/customers/${custId}/inv-profiles/${invProfileId}/details/${invProfileDetailId}/end`,
        method: "POST",
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return response;
        }
        const endRaw = response.endDate ?? response.end_date ?? "";
        const endDate =
          endRaw == null || endRaw === ""
            ? ""
            : String(endRaw).trim().includes("T")
              ? String(endRaw).trim().split("T")[0]
              : String(endRaw).trim();
        return {
          ...response,
          defaultInvoiceDescription: String(
            response.defaultInvoiceDescription ?? response.default_invoice_description ?? ""
          ).trim(),
          endDate,
          endAppliedByDisplayName: String(
            response.endAppliedByDisplayName ?? response.end_applied_by_display_name ?? ""
          ).trim(),
        };
      },
      invalidatesTags: (result, error, { custId }) =>
        custId != null ? [{ type: "ClientConfigurationInvProfileDetails", id: custId }] : [],
    }),
    createClientConfiguration: build.mutation({
      query: (body) => ({
        url: "/api/client-configuration",
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateClientConfigurationMutation,
  useUpdateClientConfigurationCustomerMutation,
  useGetClientConfigurationGroupsQuery,
  useGetClientConfigurationGroupDetailsQuery,
  useGetClientConfigurationCustomerQuery,
  useGetClientConfigurationEmployeesQuery,
  useGetClientConfigurationCustgroupAccountExecutivesQuery,
  useGetClientConfigurationCustgroupCustomerTeamOptionsQuery,
  useGetClientConfigurationJobTypesQuery,
  useGetClientConfigurationMasterTypesQuery,
  useGetClientConfigurationSubTypesQuery,
  useGetClientConfigurationJobStatusesQuery,
  useGetClientConfigurationCostCentersQuery,
  useGetClientConfigurationProductsQuery,
  useGetClientConfigurationCustgroupsDirectoryQuery,
  useGetClientConfigurationCustgroupClientOptionsQuery,
  useGetClientConfigurationJacketsQuery,
  useGetClientConfigurationActivePosQuery,
  useGetClientConfigurationCustomerJacketJobsQuery,
  useCreateClientConfigurationJacketJobMutation,
  useUpdateClientConfigurationJacketJobMutation,
  useDeleteClientConfigurationJacketJobMutation,
  useGetClientConfigurationCustomerCompanyNumbersQuery,
  useGetClientConfigurationProductDivisionsForCompanyNumberQuery,
  useGetClientConfigurationProductClassesForCompanyNumberQuery,
  useGetClientConfigurationProductCodesForCompanyNumberQuery,
  useGetClientConfigurationProductTypesForCompanyNumberQuery,
  useGetClientConfigurationProductLinesForCompanyNumberQuery,
  useGetClientConfigurationBusinessClassificationsQuery,
  useGetClientConfigurationBusinessClassificationValuesQuery,
  useGetClientConfigurationWarehousesQuery,
  useGetClientConfigurationCustomerPoOptionsQuery,
  useGetClientConfigurationCustomerContractsQuery,
  useCreateClientConfigurationContractMutation,
  useUpdateClientConfigurationContractMutation,
  useDeleteClientConfigurationContractMutation,
  useGetClientConfigurationCustomerContractServicesQuery,
  useCreateClientConfigurationContractServiceMutation,
  useUpdateClientConfigurationContractServiceMutation,
  useDeleteClientConfigurationContractServiceMutation,
  useEndClientConfigurationContractServiceMutation,
  useGetClientConfigurationCustgroupInvProfilesQuery,
  useGetClientConfigurationCustgroupInvProfileDetailsQuery,
  useGetClientConfigurationCustgroupInvProfileJobnosQuery,
  useGetClientConfigurationCustomerInvProfilesQuery,
  useGetClientConfigurationCustomerInvProfileJobnosQuery,
  useGetClientConfigurationCustomerInvProfileOpCodesForOpCodeQuery,
  useCreateClientConfigurationInvProfileMutation,
  useGetClientConfigurationContractServiceInvoiceDescriptionOptionsQuery,
  useGetClientConfigurationOperationsQuery,
  useGetClientConfigurationCustomerInvProfileDetailsQuery,
  useCreateClientConfigurationInvProfileDetailMutation,
  useUpdateClientConfigurationInvProfileDetailMutation,
  useEndClientConfigurationInvProfileDetailMutation,
  useDeactivateClientConfigurationInvProfileMutation,
} = clientConfigurationApi;
